import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { AppPicker } from './AppPicker.js';
import { Button, DataTable } from '../../../../shared/components/index.js';

interface WorkflowRow {
  readonly workflowConfigId: string;
  readonly workflowCode: string;
  readonly workflowName: string;
  readonly workflowType: string;
  readonly status: string;
  readonly versionNo: number;
}

const WORKFLOW_TYPES = ['SEQUENTIAL', 'PARALLEL', 'APPROVAL_CHAIN', 'ESCALATION', 'EXCEPTION', 'REVIEW', 'COMPLIANCE', 'CUSTOM'];
const STATUSES = ['DRAFT', 'DEPLOYED', 'ARCHIVED'];

/**
 * M40 — Workflow Designer (§10). The workflow authoring surface over T526.
 * Compose a workflow of ordered steps under an application, then deploy it to
 * the M04 engine. Viewing needs WORKFLOW_VIEW; authoring / deploy need
 * WORKFLOW_MANAGE. Deploy binds the config to an M04 definition reference.
 */
export function WorkflowDesigner(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [applicationId, setApplicationId] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [rows, setRows] = useState<WorkflowRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [workflowName, setWorkflowName] = useState('');
  const [workflowType, setWorkflowType] = useState('APPROVAL_CHAIN');
  const [stepsCsv, setStepsCsv] = useState('Manager review');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (applicationId) params.set('applicationId', applicationId);
    if (typeFilter) params.set('workflowType', typeFilter);
    if (statusFilter) params.set('status', statusFilter);
    params.set('pageSize', '100');
    apiRequest<{ data: WorkflowRow[]; total: number }>(`/v1/app-platform/workflows?${params.toString()}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, applicationId, typeFilter, statusFilter, reload]);

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!applicationId.trim()) { setActionError(t('m40.common.appIdRequired')); return; }
    if (!workflowName.trim()) { setActionError(t('m40.workflowDesigner.nameRequired')); return; }
    setBusy(true);
    const steps = stepsCsv.split(',').map((s) => s.trim()).filter(Boolean).map((name, i) => ({ order: i + 1, name }));
    try {
      const r = await apiRequest<{ data: WorkflowRow }>(`/v1/app-platform/workflows`, {
        method: 'POST',
        body: { applicationId: applicationId.trim(), workflowName: workflowName.trim(), workflowType, steps },
        token,
      });
      setNotice(t('m40.common.created', { code: r.data.workflowCode }));
      setWorkflowName('');
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m40.workflowDesigner.notAuth'));
      } else {
        setActionError((err as Error).message);
      }
    } finally {
      setBusy(false);
    }
  };

  const deploy = async (row: WorkflowRow): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/app-platform/workflows/${row.workflowConfigId}/deploy`, { method: 'POST', token });
      setNotice(t('m40.workflowDesigner.deployed', { code: row.workflowCode }));
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m40.workflowDesigner.notAuth'));
      } else {
        setActionError((err as Error).message);
      }
    }
  };

  const remove = async (row: WorkflowRow): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/app-platform/workflows/${row.workflowConfigId}`, { method: 'DELETE', token });
      setNotice(t('m40.common.removed', { code: row.workflowCode }));
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m40.workflowDesigner.notAuth'));
      } else {
        setActionError((err as Error).message);
      }
    }
  };

  return (
    <main aria-label="workflow-designer">
      <h1>{t('m40.workflowDesigner.title')}</h1>

      <section aria-label="workflow-scope">
        <AppPicker label={t('m40.common.applicationId')} value={applicationId} onPick={setApplicationId} />
      </section>

      <section aria-label="create-workflow">
        <h2>{t('m40.workflowDesigner.newWorkflow')}</h2>
        <form aria-label="workflow-form" onSubmit={(e) => void create(e)}>
          <label>{t('m40.common.name')}<input aria-label="field-workflow-name" value={workflowName} onChange={(e) => setWorkflowName(e.target.value)} /></label>
          <label>{t('m40.common.type')}
            <select aria-label="field-workflow-type" value={workflowType} onChange={(e) => setWorkflowType(e.target.value)}>
              {WORKFLOW_TYPES.map((ty) => <option key={ty} value={ty}>{ty}</option>)}
            </select>
          </label>
          <label>{t('m40.workflowDesigner.steps')}<input aria-label="field-steps" value={stepsCsv} onChange={(e) => setStepsCsv(e.target.value)} /></label>
          <Button variant="primary" type="submit" aria-label="submit-workflow" disabled={busy}>{busy ? t('m40.common.creating') : t('m40.workflowDesigner.createBtn')}</Button>
        </form>
        {actionError && <p role="alert" aria-label="workflow-error">{actionError}</p>}
        {notice && <p aria-label="workflow-notice">{notice}</p>}
      </section>

      <section aria-label="workflow-filters">
        <label>{t('m40.common.type')}
          <select aria-label="filter-type" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
            <option value="">{t('m40.common.allTypes')}</option>
            {WORKFLOW_TYPES.map((ty) => <option key={ty} value={ty}>{ty}</option>)}
          </select>
        </label>
        <label>{t('m40.common.status')}
          <select aria-label="filter-status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">{t('m40.common.allStates')}</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
      </section>

      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m40.common.loading')}</p> : (
        <>
          <p aria-label="workflow-total">{t('m40.workflowDesigner.total', { count: rows.length })}</p>
          <DataTable<WorkflowRow>
            ariaLabel="workflow-table"
            testId="workflow-table"
            rowKey={(r) => r.workflowConfigId}
            rowDataAttrs={(r) => ({ 'data-workflow': r.workflowCode })}
            emptyMessage={t('m40.workflowDesigner.empty')}
            rows={rows}
            columns={[
              { key: 'code', header: t('m40.common.code'), render: (r) => r.workflowCode },
              { key: 'name', header: t('m40.common.name'), render: (r) => r.workflowName },
              { key: 'type', header: t('m40.common.type'), render: (r) => r.workflowType },
              { key: 'status', header: t('m40.common.status'), cellAriaLabel: (r) => `status-${r.workflowCode}`, render: (r) => r.status },
              { key: 'version', header: t('m40.common.version'), render: (r) => `v${r.versionNo}` },
              { key: 'actions', header: t('m40.common.actions'), render: (r) => (
                <>
                  <Button variant="primary" type="button" aria-label={`deploy-${r.workflowCode}`} onClick={() => void deploy(r)}>{t('m40.common.deploy')}</Button>
                  <Button variant="danger" type="button" aria-label={`remove-${r.workflowCode}`} onClick={() => void remove(r)}>{t('m40.common.remove')}</Button>
                </>
              ) },
            ]}
          />
        </>
      )}
    </main>
  );
}
